import {
  createSlice, createAsyncThunk,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import { UserPermission, UserProfile } from 'constants/interface'; // TODO: Maybe move this to here
import { ApplicationError } from 'infra/error/application';
import { INTERNAL_ERROR, INVALID_CREDENTIALS, UNAUTHORIZED_ERROR } from 'infra/error/errors';
import { createAlertNotification } from 'infra/notification';
import { RootState } from 'state';
import { AlertNotificationType, setNotificationAction } from './notification';

interface UserAuthData {
  email: string;
  password: string;
}

export interface SessionState {
  profile?: UserProfile;
  permissions?: UserPermission[],
  isDataRefreshed: boolean,
  isActive: boolean;
  isLoading: boolean;
}

export const initialState: SessionState = {
  profile: undefined,
  permissions: undefined,
  isDataRefreshed: false,
  isActive: false,
  isLoading: false,
};

export const authenticateAction = createAsyncThunk(
  'session/authenticate',
  async (payload: UserAuthData, thunkAPI) => {
    try {
      const userData = await apiClient.authentication.authenticate(payload.email, payload.password);
      const successAlert = createAlertNotification(`Bem vinda(o) ${userData.name}!`, AlertNotificationType.Success);
      thunkAPI.dispatch(setNotificationAction(successAlert));
      return userData;
    } catch (err) {
      const appError = err as ApplicationError;
      const authError = appError.code === UNAUTHORIZED_ERROR.keyCode
        ? new ApplicationError(INVALID_CREDENTIALS.keyCode)
        : appError;
      throw authError;
    }
  },
);

export const refreshDataAction = createAsyncThunk(
  'session/refreshData',
  async () => {
    const userData = await apiClient.authentication.refreshData();
    return userData;
  },
);

export const logoutAction = createAsyncThunk(
  'session/logout',
  async () => {
    try {
      await apiClient.authentication.logout();
    } catch (err) {
      throw new ApplicationError(INTERNAL_ERROR.keyCode);
    }
  },
);

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setUserProfileAction: (state, action) => {
      state.profile = action.payload;
    },
    setPermissionsAction: (state, action) => {
      state.permissions = action.payload;
    },
    cleanSessionAction: (state) => {
      state.isActive = false;
      state.profile = undefined;
      state.permissions = undefined;
    },
  },
  extraReducers: (builder) => {
    // Authenticate Action
    builder.addCase(authenticateAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(authenticateAction.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isActive = true;
      state.profile = {
        id: action.payload.id,
        name: action.payload.name,
        email: action.payload.email,
      };
      state.permissions = action.payload.permissions;
    });
    builder.addCase(authenticateAction.rejected, (state) => {
      state.isLoading = false;
      // TODO: Check if middleware would handle this
      state.isActive = false;
      state.profile = undefined;
      state.permissions = undefined;
    });

    // Refresh data Action
    builder.addCase(refreshDataAction.fulfilled, (state, action) => {
      state.isDataRefreshed = true;
      state.profile = {
        id: action.payload.id,
        name: action.payload.name,
        email: action.payload.email,
      };
      state.permissions = action.payload.permissions;
      state.isLoading = false;
      state.isActive = true;
    });
    builder.addCase(refreshDataAction.rejected, (state) => {
      state.isDataRefreshed = true;
      // TODO: Check if middleware would handle this
      state.isActive = false;
      state.profile = undefined;
      state.permissions = undefined;
    });

    // Logout Action
    builder.addCase(logoutAction.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(logoutAction.fulfilled, (state) => {
      state.isLoading = false;
      state.isActive = false;
      state.profile = undefined;
      state.permissions = undefined;
    });
    builder.addCase(logoutAction.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const {
  setUserProfileAction,
  setPermissionsAction,
  cleanSessionAction,
} = sessionSlice.actions;

export const userProfileSelector = (state: RootState):
  UserProfile | undefined => state.session.profile;

export const userPermissionsSelector = (state: RootState):
  UserPermission[] | undefined => state.session.permissions;

export const sessionIsActiveSelector = (state: RootState):
  boolean => state.session.isActive;

export const sessionIsLoadingSelector = (state: RootState):
  boolean => state.session.isLoading;

export const userDataRefreshedSelector = (state: RootState):
  boolean | undefined => state.session.isDataRefreshed;
